/**
 * Shared geometry for dashboard screenshot thumbnails, used by every surface that renders a
 * `screenshot_url` (the /dashboards list cards, the viewer's My Dashboards popover). Keeping the
 * ratio test and the fade math in one place means a screenshot classifies identically everywhere.
 */

// The thumbnail box ratio, per Figma 6429:8338 / 6432:8370 (320x180). New captures are generated
// at 1440x810 to fill it exactly; anything else is a legacy capture.
export const THUMBNAIL_ASPECT_RATIO = 16 / 9;

// A screenshot within this much of the box ratio counts as matching. Wide enough to absorb rounding
// in stored capture dimensions, narrow enough to keep 3:2 (1.5) and 2:1 (2.0) distinct from 1.778.
export const ASPECT_RATIO_TOLERANCE = 0.02;

// Height of the bottom fades, as a fraction of the box: 66px over Figma's 180px-tall box.
export const FADE_HEIGHT_FRACTION = 66 / 180;

// The dark bottom gradient starts 113/180 down the box and ends at 20% black on the bottom edge
// (Figma "Rectangle 149358").
export const DARK_FADE_TOP_PERCENT = (113 / 180) * 100;

/**
 * Where a legacy screenshot's bottom edge lands, as a percentage of the box's height, once it has
 * been scaled to the box's width. Returns undefined when the source already matches the box ratio
 * and so needs neither the width-fit nor the fades.
 *
 * A source taller than the box would land past 100%; it is clamped so the fade still ends on the
 * box's bottom edge rather than off it.
 */
export const getLegacyFadeStopPercent = (
  naturalWidth: number,
  naturalHeight: number
): number | undefined => {
  if (naturalWidth <= 0 || naturalHeight <= 0) {
    return undefined;
  }
  const ratio = naturalWidth / naturalHeight;
  if (Math.abs(ratio - THUMBNAIL_ASPECT_RATIO) <= ASPECT_RATIO_TOLERANCE) {
    return undefined;
  }
  return Math.min(100, (THUMBNAIL_ASPECT_RATIO / ratio) * 100);
};
